<script lang="ts" setup>
import { useI18n } from 'vue-i18n'
import { onBeforeMount, ref } from 'vue'
import axios from 'axios'
import { notify } from '@kyvg/vue3-notification'

import useForm from '@/hooks/use-form'
import { FieldPreference } from '@/types/general'
import { DriverReportPreferences, SettlementTemplate } from '@/types/driver-report'
import { useAuthStore } from '@/stores/auth-store'
import { LicenseType } from '@/types/company'
import { useConfirm } from '@/hooks/use-confirm'
import { MyButtonScheme } from '@/types/layout/my-button'
import { useCompanyStore } from '@/stores/company-store'

import ManageSettlementTemplateModal from '@/components/driver-reports/ManageSettlementTemplateModal.vue'
import MyButton from '@/components/my-components/MyButton.vue'
import MyRadioButtonGroup from '@/components/my-components/form/MyRadioButtonGroup.vue'
import MyRadioButton from '@/components/my-components/form/MyRadioButton.vue'
import MyForm from '@/components/my-components/form/MyForm.vue'

const authStore = useAuthStore()
const companyStore = useCompanyStore()
const { t } = useI18n()
const confirm = useConfirm()

const form = useForm<DriverReportPreferences>({
    tourType: FieldPreference.Required,
    referenceNumber: FieldPreference.Optional,
    kilometerCountImage: FieldPreference.Optional,
    tourPicker: FieldPreference.Optional,
    vehicle: FieldPreference.Optional,
    units: FieldPreference.Optional,
    kilometerCount: FieldPreference.Optional,
    departureNote: FieldPreference.Optional,
    shipmentLoadUnload: FieldPreference.Optional,
    referenceNumberPromptWhenEndingStop: false,
    driverExpectedEta: false,
    transportObjectPickerContractorFilter: false,
})

const settlementTemplateModalOpen = ref(false)
const selectedSettlementTemplate = ref<SettlementTemplate>()

async function savePreferences() {
    const response = await form.submit(
        'PUT',
        window.route('dr.company.preferences.update', { company: authStore.companyId }),
    )

    if (response) {
        authStore.licenses[LicenseType.DriverReportModule] = {
            ...authStore.licenses[LicenseType.DriverReportModule],
            ...form.data,
        }

        notify({
            title: t('licensePreferencesUpdated'),
            type: 'success',
        })
    }
}

function editSettlementTemplate(template: SettlementTemplate) {
    selectedSettlementTemplate.value = template
    settlementTemplateModalOpen.value = true
}

async function deleteSettlementTemplate(template: SettlementTemplate) {
    try {
        await confirm(
            t('deleteEntityTitle', { entity: t('settlementTemplate') }),
            t('deleteEntityDescription', { entity: t('settlementTemplate') }),
            {
                confirmText: t('yes'),
                cancelText: t('no'),
                confirmButtonScheme: MyButtonScheme.Warning,
            },
        )
    } catch {
        return
    }

    companyStore.loadingSettlementTemplates = true
    await axios.delete(
        window.route('dr.company.settlement-templates.destroy', [authStore.companyId, template.id]),
    )
    notify({ title: t('entityDeleted', { entity: t('settlementTemplate') }), type: 'success' })
    companyStore.fetchSettlementTemplates()
}

onBeforeMount(() => {
    companyStore.fetchSettlementTemplates()
    const prefs: Partial<DriverReportPreferences> =
        authStore.licenses[LicenseType.DriverReportModule] ?? {}

    form.reset({ ...prefs })
})
</script>

<template>
    <MyForm class="space-y-2" :errors="form.errors.value" @submit.prevent="savePreferences">
        <h3
            class="text-sm font-bold uppercase tracking-wide text-primary-400 dark:text-primary-300"
            v-text="t('settlementTemplates')"
        />

        <div
            v-for="template in companyStore.settlementTemplates"
            :key="template.id"
            class="flex items-center justify-between"
        >
            {{ template.name }}

            <div class="flex">
                <MyButton
                    v-tooltip="t('edit')"
                    type="button"
                    class="ml-2 flex h-6 w-6 items-center justify-center rounded-lg bg-transparent text-xs text-primary-500 drop-shadow-lg hover:bg-primary-200 dark:hover:bg-dark-800"
                    icon
                    size="micro"
                    scheme="primary"
                    @click="editSettlementTemplate(template)"
                >
                    <mdi:pencil />
                </MyButton>

                <MyButton
                    v-tooltip="t('delete')"
                    type="button"
                    class="ml-2 flex h-6 w-6 items-center justify-center rounded-lg bg-transparent text-xs text-red-500 drop-shadow-lg hover:bg-primary-200 dark:hover:bg-dark-800"
                    icon
                    size="micro"
                    scheme="danger"
                    @click="deleteSettlementTemplate(template)"
                >
                    <mdi:trash-can />
                </MyButton>
            </div>
        </div>

        <MyButton
            scheme="primary"
            type="button"
            class="w-full h-12 text-base font-bold"
            @click="settlementTemplateModalOpen = true"
        >
            {{ t('createNew') }}
        </MyButton>

        <h3
            class="relative text-sm font-bold uppercase tracking-wide text-primary-400 dark:text-primary-300"
            v-text="t('fields')"
        />

        <!-- Preferences Form Fields -->

        <MyRadioButtonGroup v-model="form.data.tourType" :label="t('tourTypeDescribed')">
            <MyRadioButton :label="t('required')" :value="FieldPreference.Required" />
            <MyRadioButton :label="t('hidden')" :value="FieldPreference.Hidden" />
        </MyRadioButtonGroup>

        <MyRadioButtonGroup
            v-if="authStore.hasLicense(LicenseType.DeliveryManagementModule)"
            v-model="form.data.tourPicker"
            class="relative"
            :label="t('showTourPicker')"
        >
            <MyRadioButton :label="t('required')" :value="FieldPreference.Required" />
            <MyRadioButton :label="t('optional')" :value="FieldPreference.Optional" />
            <MyRadioButton :label="t('hidden')" :value="FieldPreference.Hidden" />

            <template #label>
                {{ t('showTourPicker') }}
                <mdi:information
                    v-tooltip="t('showTourPickerExplanation')"
                    class="absolute top-1 right-0"
                />
            </template>
        </MyRadioButtonGroup>

        <MyRadioButtonGroup v-model="form.data.referenceNumber" :label="t('referenceNumber')">
            <MyRadioButton :label="t('required')" :value="FieldPreference.Required" />
            <MyRadioButton :label="t('optional')" :value="FieldPreference.Optional" />
            <MyRadioButton :label="t('hidden')" :value="FieldPreference.Hidden" />
        </MyRadioButtonGroup>

        <MyRadioButtonGroup v-model="form.data.vehicle" :label="t('vehicle')">
            <MyRadioButton :label="t('required')" :value="FieldPreference.Required" />
            <MyRadioButton :label="t('optional')" :value="FieldPreference.Optional" />
            <MyRadioButton :label="t('hidden')" :value="FieldPreference.Hidden" />
        </MyRadioButtonGroup>

        <MyRadioButtonGroup v-model="form.data.units" :label="t('units')">
            <MyRadioButton :label="t('required')" :value="FieldPreference.Required" />
            <MyRadioButton :label="t('optional')" :value="FieldPreference.Optional" />
            <MyRadioButton :label="t('hidden')" :value="FieldPreference.Hidden" />
        </MyRadioButtonGroup>

        <MyRadioButtonGroup v-model="form.data.kilometerCount" :label="t('kilometerCount')">
            <MyRadioButton :label="t('required')" :value="FieldPreference.Required" />
            <MyRadioButton :label="t('optional')" :value="FieldPreference.Optional" />
            <MyRadioButton :label="t('hidden')" :value="FieldPreference.Hidden" />
        </MyRadioButtonGroup>

        <MyRadioButtonGroup
            v-if="form.data.kilometerCount !== FieldPreference.Hidden"
            v-model="form.data.kilometerCountImage"
            :label="t('kilometerCountImage')"
        >
            <MyRadioButton :label="t('required')" :value="FieldPreference.Required" />
            <MyRadioButton :label="t('optional')" :value="FieldPreference.Optional" />
        </MyRadioButtonGroup>

        <MyRadioButtonGroup v-model="form.data.departureNote" :label="t('departureNote')">
            <MyRadioButton :label="t('required')" :value="FieldPreference.Required" />
            <MyRadioButton :label="t('optional')" :value="FieldPreference.Optional" />
            <MyRadioButton :label="t('hidden')" :value="FieldPreference.Hidden" />
        </MyRadioButtonGroup>

        <MyRadioButtonGroup
            v-model="form.data.shipmentLoadUnload"
            class="relative"
            :label="t('shipmentLoadUnload')"
        >
            <template #label>
                {{ t('shipmentLoadUnload') }}
                <mdi:information
                    v-tooltip="t('shipmentLoadUnloadExplanation')"
                    class="absolute top-1 right-0"
                />
            </template>
            <MyRadioButton :label="t('required')" :value="FieldPreference.Required" />
            <MyRadioButton :label="t('optional')" :value="FieldPreference.Optional" />
            <MyRadioButton :label="t('hidden')" :value="FieldPreference.Hidden" />
        </MyRadioButtonGroup>

        <MyRadioButtonGroup
            v-model="form.data.referenceNumberPromptWhenEndingStop"
            class="relative"
            :label="t('referencePromptWhenEndingStop')"
        >
            <MyRadioButton :label="t('enabled')" :value="true" />
            <MyRadioButton :label="t('disabled')" :value="false" />

            <template #label>
                {{ t('referencePromptWhenEndingStop') }}
                <mdi:information
                    v-tooltip="t('referencePromptWhenEndingStopExplanation')"
                    class="absolute top-1 right-0"
                />
            </template>
        </MyRadioButtonGroup>

        <MyRadioButtonGroup
            v-if="authStore.hasLicense(LicenseType.DeliveryManagementModule)"
            v-model="form.data.driverExpectedEta"
            class="relative"
            :label="t('driverExpectedEtaPrompt')"
        >
            <MyRadioButton :label="t('enabled')" :value="true" />
            <MyRadioButton :label="t('disabled')" :value="false" />

            <template #label>
                {{ t('driverExpectedEtaPrompt') }}
                <mdi:information
                    v-tooltip="t('driverExpectedEtaPromptExplanation')"
                    class="absolute top-1 right-0"
                />
            </template>
        </MyRadioButtonGroup>

        <MyRadioButtonGroup
            v-model="form.data.transportObjectPickerContractorFilter"
            class="relative"
        >
            <MyRadioButton :label="t('enabled')" :value="true" />
            <MyRadioButton :label="t('disabled')" :value="false" />

            <template #label>
                {{ t('contractorFilter') }}
                <mdi:information
                    v-tooltip="t('contractorFilterExplanation')"
                    class="absolute top-1 right-0"
                />
            </template>
        </MyRadioButtonGroup>

        <!-- Save Button -->
        <div class="flex justify-end pt-4">
            <MyButton
                type="submit"
                scheme="primary"
                :loading="form.loading.value"
                :disabled="form.loading.value"
            >
                {{ t('save') }}
            </MyButton>
        </div>
        <ManageSettlementTemplateModal
            v-model="settlementTemplateModalOpen"
            :settlement-template="selectedSettlementTemplate"
            @saved="companyStore.fetchSettlementTemplates()"
        />
    </MyForm>
</template>
